// @ts-nocheck
import { MODULE_ACL, USER_ROLES } from "./constant";
import { isAclModuleOptionsRelevant } from "./aclModuleOptionsAccess";
import { parseModuleOperations } from "./voicePlanAcl";

export const IVR_ACL_MODULE = MODULE_ACL.IVR;

export const IVR_ALWAYS_ALLOWED_ACTION_TYPES = new Set(["answer", "hangup"]);

export const IVR_NODE_OPTION_DEFS = [
  {
    value: "play",
    label: "Play",
    description: "Add Play nodes to IVR flows",
  },
  {
    value: "dtmf",
    label: "DTMF",
    description: "Add DTMF / Gather nodes to IVR flows",
  },
  {
    value: "record",
    defaultOn: false,
    label: "Record",
    description: "Add Record nodes to IVR flows",
  },
  {
    value: "transfer",
    label: "Transfer",
    description: "Add Transfer nodes to IVR flows",
  },
  {
    value: "voicemail",
    defaultOn: false,
    label: "Voicemail",
    description: "Add Voicemail nodes to IVR flows",
  },
  {
    value: "api",
    label: "API",
    description: "Add API nodes to IVR flows",
  },
  {
    value: "missed_ivr",
    defaultOn: false,
    label: "Missed Type IVR",
    description: "Create and edit Missed category IVR flows",
  },
  {
    value: "language",
    defaultOn: false,
    label: "Language",
    description: "Add Language routing nodes to IVR flows",
  },
  {
    value: "conditional",
    defaultOn: false,
    label: "Conditional",
    description: "Add Conditional nodes to IVR flows",
  },
  {
    value: "ai_agent",
    defaultOn: false,
    label: "AI Agent",
    description: "Add AI Agent nodes to IVR flows",
  },
];

const ALL_OPTION_VALUES = IVR_NODE_OPTION_DEFS.map((o) => o.value);

export const DEFAULT_IVR_NODE_OPTIONS = IVR_NODE_OPTION_DEFS.filter(
  (o) => o.defaultOn !== false
).map((o) => o.value);

function isSuperAdminRole(userRole, isSuperAdmin) {
  return isSuperAdmin || userRole === USER_ROLES.SUPERADMIN;
}

export function getIvrAclEntry(moduleOperations) {
  const ops = parseModuleOperations(moduleOperations);
  return ops[IVR_ACL_MODULE] || null;
}

export function normalizeIvrNodeOptions(options) {
  if (!Array.isArray(options)) return [];
  return [
    ...new Set(
      options
        .map((o) => String(o).trim())
        .filter((o) => ALL_OPTION_VALUES.includes(o))
    ),
  ];
}

export function getAllowedIvrNodeOptions(
  moduleOperations,
  { userRole = null, isSuperAdmin = false } = {}
) {
  if (isSuperAdminRole(userRole, isSuperAdmin)) {
    return ALL_OPTION_VALUES;
  }

  const entry = getIvrAclEntry(moduleOperations);
  if (!entry) {
    // No IVR entry in user's ACL — no restriction, all options grantable
    return null;
  }

  if (!entry.edit && !entry.add) {
    return [];
  }

  const options = normalizeIvrNodeOptions(entry.edit_options);
  if (options.length === 0) {
    return null;
  }

  return options;
}

export function canUseIvrNode(
  moduleOperations,
  optionKey,
  { userRole = null, isSuperAdmin = false } = {}
) {
  if (isSuperAdminRole(userRole, isSuperAdmin)) {
    return true;
  }

  const allowed = getAllowedIvrNodeOptions(moduleOperations, {
    userRole,
    isSuperAdmin,
  });
  if (allowed === null) {
    return true;
  }
  return allowed.includes(optionKey);
}

export function paletteActionTypeToAclKey(actionType) {
  if (actionType === "gather") return "dtmf";
  if (actionType === "aiAgent") return "ai_agent";
  return actionType;
}

export function canUseIvrPaletteAction(
  moduleOperations,
  actionType,
  context = {}
) {
  if (IVR_ALWAYS_ALLOWED_ACTION_TYPES.has(actionType)) {
    return true;
  }
  const aclKey = paletteActionTypeToAclKey(actionType);
  return canUseIvrNode(moduleOperations, aclKey, context);
}

export function canAccessMissedIvr(moduleOperations, context = {}) {
  return canUseIvrNode(moduleOperations, "missed_ivr", context);
}

export function isIvrNodeAclConfigured(moduleEntry) {
  if (!moduleEntry?.edit && !moduleEntry?.add) return false;
  return normalizeIvrNodeOptions(moduleEntry.edit_options).length > 0;
}

export function getIvrNodeOptionsForEditor(moduleOperations, isSuperAdmin) {
  const allowed = getAllowedIvrNodeOptions(moduleOperations, { isSuperAdmin });
  if (isSuperAdmin || allowed === null) {
    return IVR_NODE_OPTION_DEFS;
  }
  return IVR_NODE_OPTION_DEFS.filter((o) => allowed.includes(o.value));
}

export function getParentGrantableIvrNodeOptions(
  moduleOperations,
  isSuperAdmin
) {
  if (isSuperAdmin) return ALL_OPTION_VALUES;
  const allowed = getAllowedIvrNodeOptions(moduleOperations, {
    isSuperAdmin: false,
  });
  if (allowed === null) {
    return ALL_OPTION_VALUES;
  }
  return allowed;
}

export function normalizeIvrNodeOptionsForSave(
  options,
  parentGrantable = null
) {
  const pool =
    parentGrantable && parentGrantable.length > 0
      ? parentGrantable
      : ALL_OPTION_VALUES;
  return normalizeIvrNodeOptions(options).filter((o) => pool.includes(o));
}

export function formatIvrNodeOptionsLabel(moduleEntry) {
  if (!isAclModuleOptionsRelevant(moduleEntry)) return "—";
  const options = normalizeIvrNodeOptions(moduleEntry.edit_options);
  if (options.length === 0) return "—";
  if (options.length === ALL_OPTION_VALUES.length) {
    return "All options";
  }
  return `${options.length} option(s)`;
}

export function getUserModuleOperations(user) {
  if (!user) return {};
  const raw =
    user?.acl_settings?.module_operations ?? user?.module_operations ?? null;
  return parseModuleOperations(raw);
}
